import React from 'react';
import SEO from '@/components/layout/SEO';
import { MaterialIcon } from '@/components/ui/MaterialIcon';

const faqSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What is startzone.dev?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'startzone.dev is a gamified learning platform built for developers. You earn XP, lose lives on mistakes, and keep streaks alive to unlock better rewards.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I earn XP on startzone.dev?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'You earn XP by answering questions correctly, clearing modules, finishing courses, and keeping up your streak.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do lives work on startzone.dev?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'You begin with 3 lives. Wrong answers cost 1 life, and lives regenerate over time or can be restored with XP.',
            },
        },
    ],
});

type AchievementBadge = {
    icon: string;
    title: string;
    accent: string;
    filled?: boolean;
    locked?: boolean;
};

const achievementBadges: AchievementBadge[] = [
    { icon: 'terminal', title: 'First Command', accent: 'text-[#8cacff]' },
    { icon: 'database', title: 'Data Miner', accent: 'text-[#00cffc]' },
    { icon: 'grade', title: 'System Overlord', accent: 'text-[#d674ff]', filled: true },
    { icon: 'lock', title: 'Locked', accent: 'text-[#767575]', locked: true },
    { icon: 'lock', title: 'Locked', accent: 'text-[#767575]', locked: true },
] ;

const timeline = [
    {
        level: 'LVL_01: INITIATE',
        title: 'CORE ACCESS',
        body: 'Entry into the StartZone. Basic quiz access and personal dashboard unlocked. Start your journey.',
        tags: ['Basic Terminal', 'Novice Badge'],
        accent: 'primary',
    },
    {
        level: 'LVL_10: OPERATIVE',
        title: 'ADVANCED LOGIC',
        body: 'Unlock the "Intel" module. Gain access to complex architectural challenges and custom avatars.',
        tags: ['Intel Feed', 'Skin Customizer'],
        accent: 'secondary',
    },
    {
        level: 'LVL_50: OVERLORD',
        title: 'VAULT CLEARANCE',
        body: 'Master level. Full access to the "Vault" and priority ranking in global leadboards.',
        tags: ['The Vault', 'Leaderboard King'],
        accent: 'tertiary',
    },
] as const;

export default function GameGuidePage() {
    return (
        <div className="pb-24 pt-10">
            <SEO
                title="STARTZONE.DEV | TACTICAL_MANUAL"
                description="Operational guide for startzone.dev."
                schema={faqSchema}
                canonicalUrl="https://startzone.dev/game-guide"
            />

            <div
                className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(72,72,72,0.28) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                }}
            >
                <section className="mb-20">
                    <div className="mb-4 flex flex-col items-baseline gap-4 md:flex-row">
                        <span className="font-['JetBrains_Mono'] text-sm uppercase tracking-[0.3em] text-[#00cffc]">
                            [PROTOCOL_INITIATED]
                        </span>
                        <h1 className="font-['Space_Grotesk'] text-5xl font-black uppercase tracking-tighter text-[#e8eaf0] md:text-8xl">
                            OPERATIONAL GUIDE
                        </h1>
                    </div>
                    <div className="mb-8 h-px bg-gradient-to-r from-[#00cffc] to-transparent" />
                    <p className="max-w-2xl text-lg leading-relaxed text-[#acabaa]">
                        Welcome to the StartZone Neural Network. This documentation outlines the tactical parameters for data acquisition, system progression, and survival. Follow the directives to maximize XP output.
                    </p>
                </section>

                <section className="mb-20 grid grid-cols-1 gap-6 md:grid-cols-12">
                    <div className="relative overflow-hidden bg-[#131313] p-8 md:col-span-8">
                        <div className="absolute right-0 top-0 p-4 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[#767575] opacity-40">
                            REF_ID: 0X_XP_BAR
                        </div>
                        <div className="mb-6 flex items-center gap-3">
                            <MaterialIcon icon="bolt" size={40} fill className="text-[#8cacff]" />
                            <h2 className="font-['Space_Grotesk'] text-3xl font-bold uppercase tracking-tight">
                                Experience Points (XP)
                            </h2>
                        </div>
                        <p className="mb-10 max-w-xl text-[#acabaa]">
                            XP is the primary currency for clearance level upgrades. Every successful execution increases your synaptic bandwidth.
                        </p>

                        <div className="space-y-8">
                            <div className="space-y-2">
                                <div className="flex justify-between font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest text-[#8cacff]">
                                    <span>CURRENT_MAINFRAME_LEVEL: 42</span>
                                    <span>8,450 / 10,000 XP</span>
                                </div>
                                <div className="h-4 w-full bg-[#252626]">
                                    <div className="h-full w-[84.5%] bg-[#8cacff] shadow-[0_0_15px_rgba(140,172,255,0.6)]" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <div className="bg-[#1f2020] p-4 shadow-[inset_2px_0_0_0_#8cacff]">
                                    <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] text-[#8cacff]">TASK_COMPLETE</span>
                                    <span className="font-['Space_Grotesk'] text-xl font-bold text-[#e8eaf0]">+50 XP</span>
                                </div>
                                <div className="bg-[#1f2020] p-4 shadow-[inset_2px_0_0_0_#8cacff]">
                                    <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] text-[#8cacff]">STREAK_BONUS</span>
                                    <span className="font-['Space_Grotesk'] text-xl font-bold text-[#e8eaf0]">x1.5 MULTI</span>
                                </div>
                                <div className="bg-[#1f2020] p-4 shadow-[inset_2px_0_0_0_#8cacff]">
                                    <span className="mb-1 block font-['JetBrains_Mono'] text-[10px] text-[#8cacff]">CRITICAL_HIT</span>
                                    <span className="font-['Space_Grotesk'] text-xl font-bold text-[#e8eaf0]">+200 XP</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col justify-between border-t-4 border-[#ff716c] bg-[#131313] p-8 md:col-span-4">
                        <div>
                            <div className="mb-6 flex items-center gap-3">
                                <MaterialIcon icon="favorite" size={40} fill className="text-[#ff716c]" />
                                <h2 className="font-['Space_Grotesk'] text-3xl font-bold uppercase tracking-tight">
                                    Vitality
                                </h2>
                            </div>
                            <p className="mb-8 text-sm text-[#acabaa]">
                                System stability is finite. Consecutive failures will trigger a manual terminal reboot.
                            </p>
                        </div>

                        <div className="mb-8 flex gap-4">
                            <MaterialIcon icon="favorite" size={56} fill className="text-[#ff716c]" />
                            <MaterialIcon icon="favorite" size={56} fill className="text-[#ff716c]" />
                            <MaterialIcon icon="favorite" size={56} className="text-[#484848]" />
                        </div>

                        <div className="bg-[#9f0519]/10 p-4 shadow-[inset_2px_0_0_0_#ff716c]">
                            <p className="font-['JetBrains_Mono'] text-[10px] uppercase leading-tight text-[#ff716c]">
                                Warning: Integrity at 66%. Failure on next operation will consume remaining reserve.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="mb-20 grid grid-cols-1 gap-6 md:grid-cols-3">
                    <div className="bg-[#131313] p-6">
                        <div className="mb-6 flex items-center gap-6">
                            <div className="flex h-20 w-20 items-center justify-center bg-[#d674ff]/10 shadow-[inset_0_0_0_1px_rgba(214,116,255,0.25)]">
                                <MaterialIcon icon="local_fire_department" size={40} fill className="text-[#d674ff]" />
                            </div>
                            <div>
                                <h3 className="font-['Space_Grotesk'] text-sm font-bold uppercase tracking-wide text-[#d674ff]">
                                    Streak_Active
                                </h3>
                                <p className="font-['Space_Grotesk'] text-4xl font-black tracking-tighter text-[#e8eaf0]">
                                    14 DAYS
                                </p>
                                <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest text-[#acabaa]">
                                    +12% SPEED_BOOST
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#131313] p-6 md:col-span-2">
                        <div className="mb-6 flex items-center justify-between">
                            <h3 className="font-['Space_Grotesk'] text-sm font-bold uppercase tracking-widest text-[#00cffc]">
                                Achievement_Log
                            </h3>
                            <span className="cursor-pointer font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest text-[#767575] transition-colors hover:text-[#00cffc]">
                                VIEW_ALL_PROTOCOLS
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            {achievementBadges.map((badge) => (
                                <div
                                    key={badge.title}
                                    className={[
                                        'flex h-12 w-12 items-center justify-center bg-[#252626] shadow-[inset_0_0_0_1px_rgba(72,72,72,0.18)]',
                                        badge.locked ? 'opacity-20' : '',
                                        badge.filled ? 'bg-[#d674ff]/20' : '',
                                    ].join(' ')}
                                    title={badge.title}
                                >
                                    <MaterialIcon
                                        icon={badge.icon}
                                        size={18}
                                        fill={badge.filled}
                                        className={badge.accent}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mb-20">
                    <h2 className="mb-12 flex items-center gap-4 font-['Space_Grotesk'] text-4xl font-black uppercase">
                        <span className="text-[#00cffc]">01.</span> EXECUTION_FLOW
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3">
                        <div className="bg-[#1f2020] p-10 md:border-r md:border-[#484848]/20">
                            <span className="mb-4 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest text-[#00cffc]">
                                STEP_01
                            </span>
                            <div className="mb-6 flex h-16 w-16 items-center justify-center bg-[#00cffc]/10">
                                <MaterialIcon icon="quiz" size={30} className="text-[#00cffc]" />
                            </div>
                            <h3 className="mb-4 font-['Space_Grotesk'] text-xl font-bold uppercase text-[#e8eaf0]">
                                THE QUIZ
                            </h3>
                            <p className="text-sm leading-relaxed text-[#acabaa]">
                                Engage with logic gates and architectural puzzles. Speed and precision dictate the output quality.
                            </p>
                        </div>

                        <div className="bg-[#1f2020] p-10 md:border-r md:border-[#484848]/20">
                            <span className="mb-4 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest text-[#8cacff]">
                                STEP_02
                            </span>
                            <div className="mb-6 flex h-16 w-16 items-center justify-center bg-[#8cacff]/10">
                                <MaterialIcon icon="trending_up" size={30} className="text-[#8cacff]" />
                            </div>
                            <h3 className="mb-4 font-['Space_Grotesk'] text-xl font-bold uppercase text-[#e8eaf0]">
                                DATA HARVEST (XP)
                            </h3>
                            <p className="text-sm leading-relaxed text-[#acabaa]">
                                Successful solutions convert to raw experience points. Streaks add cumulative multipliers.
                            </p>
                        </div>

                        <div className="bg-[#1f2020] p-10">
                            <span className="mb-4 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest text-[#d674ff]">
                                STEP_03
                            </span>
                            <div className="mb-6 flex h-16 w-16 items-center justify-center bg-[#d674ff]/10">
                                <MaterialIcon icon="keyboard_double_arrow_up" size={30} className="text-[#d674ff]" />
                            </div>
                            <h3 className="mb-4 font-['Space_Grotesk'] text-xl font-bold uppercase text-[#e8eaf0]">
                                LEVEL UP
                            </h3>
                            <p className="text-sm leading-relaxed text-[#acabaa]">
                                Unlock advanced terminal commands, exclusive gear, and higher clearance tiers.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="mb-20">
                    <h2 className="mb-12 flex items-center gap-4 font-['Space_Grotesk'] text-4xl font-black uppercase">
                        <span className="text-[#d674ff]">02.</span> MILESTONE_SEQUENCE
                    </h2>
                    <div className="relative ml-4 space-y-16 border-l border-[#484848]/30 pl-8 md:ml-12">
                        {timeline.map((item) => (
                            <div key={item.level} className="relative">
                                <div className={`absolute -left-[41px] top-0 flex h-5 w-5 rotate-45 items-center justify-center bg-[#0e0e0e] border-2 ${
                                    item.accent === 'primary' ? 'border-[#8cacff]' : item.accent === 'secondary' ? 'border-[#00cffc]' : 'border-[#d674ff]'
                                }`}>
                                    <div
                                        className={`h-1.5 w-1.5 ${
                                            item.accent === 'primary'
                                                ? 'bg-[#8cacff]'
                                                : item.accent === 'secondary'
                                                    ? 'bg-[#00cffc]'
                                                    : 'bg-[#d674ff]'
                                        }`}
                                    />
                                </div>
                                <div>
                                    <span className={`mb-2 block font-['JetBrains_Mono'] text-[10px] uppercase tracking-widest ${
                                        item.accent === 'primary' ? 'text-[#8cacff]' : item.accent === 'secondary' ? 'text-[#00cffc]' : 'text-[#d674ff]'
                                    }`}>
                                        {item.level}
                                    </span>
                                    <h4 className="mb-4 font-['Space_Grotesk'] text-2xl font-bold uppercase text-[#e8eaf0]">
                                        {item.title}
                                    </h4>
                                    <p className="max-w-xl text-[#acabaa]">
                                        {item.body}
                                    </p>
                                    <div className="mt-4 flex gap-2">
                                        {item.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className={`px-2 py-1 text-[10px] font-['JetBrains_Mono'] uppercase ${
                                                    item.accent === 'primary'
                                                        ? 'bg-[#8cacff]/10 text-[#8cacff]'
                                                        : item.accent === 'secondary'
                                                            ? 'bg-[#00cffc]/10 text-[#00cffc]'
                                                            : 'bg-[#d674ff]/10 text-[#d674ff]'
                                                }`}
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mb-20 flex flex-col items-center overflow-hidden bg-[#191a1a] p-12 text-center">
                    <div className="mb-8 rounded-full bg-[#8cacff]/5 p-4 shadow-[0_0_0_1px_rgba(140,172,255,0.2)]">
                        <MaterialIcon icon="rocket_launch" size={56} className="text-[#8cacff]" fill />
                    </div>
                    <h2 className="mb-4 font-['Space_Grotesk'] text-4xl font-black uppercase">
                        SYSTEM_READY_FOR_ENGAGEMENT
                    </h2>
                    <p className="mb-10 max-w-lg italic text-[#acabaa]">
                        "The only failure is the refusal to execute."
                    </p>
                    <button
                        type="button"
                        className="bg-gradient-to-r from-[#8cacff] to-[#769dff] px-12 py-4 font-['Space_Grotesk'] text-lg font-black uppercase tracking-widest text-[#001f55] shadow-[0_0_30px_rgba(140,172,255,0.3)] transition-all hover:scale-105 active:scale-95"
                    >
                        LAUNCH_CHALLENGE_01
                    </button>
                </section>
            </div>
        </div>
    );
}
